/**
 * Timeline Section — Infinite-look carousel
 * Cloned items pad both sides so images are always visible.
 * Auto-progression (3s), click-to-activate.
 * When wrapping last→first, animates forward to clone then snaps back seamlessly.
 * Progress bar fills to the active label position.
 */

class Timeline {
  constructor(element) {
    this.element = element;
    this.track = element.querySelector('.timeline__carousel-track');
    this.originalItems = Array.from(element.querySelectorAll('.timeline__item'));
    this.labels = Array.from(element.querySelectorAll('.timeline__label'));
    this.progressActive = element.querySelector('.timeline__progress-active');
    this.progressBar = element.querySelector('.timeline__progress');
    this.infoEl = element.querySelector('.timeline__item-info');
    this.infoTitle = element.querySelector('.timeline__item-title');
    this.infoDesc = element.querySelector('.timeline__item-description');
    this.totalSteps = this.originalItems.length;
    this.activeIndex = 0;
    this.isSnapping = false;

    // Autoplay
    this.autoplayInterval = null;
    const speedAttr = parseInt(element.dataset.autoplaySpeed, 10);
    this.autoplayDelay = (!isNaN(speedAttr) && speedAttr > 0) ? speedAttr * 1000 : 2000;
    this.autoplayTimeout = null;

    this.initialize();
  }

  initialize() {
    if (this.totalSteps === 0) return;

    this.allTrackItems = Array.from(this.originalItems);

    this.setActiveStep(0, false);

    // Click on items
    this.allTrackItems.forEach((item, i) => {
      item.addEventListener('click', () => {
        if (i !== this.activeIndex) {
          this.pauseAutoplay();
          this.setActiveStep(i);
        }
      });
    });

    // Click on labels
    this.labels.forEach((label, i) => {
      label.addEventListener('click', () => {
        if (i !== this.activeIndex) {
          this.pauseAutoplay();
          this.setActiveStep(i);
        }
      });
    });

    this.setupAutoplayObserver();
  }

  /* -------------------------------------------------------
     Autoplay & observer
     ------------------------------------------------------- */
  setupAutoplayObserver() {
    var self = this;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            self.startAutoplay();
          } else {
            self.stopAutoplay();
          }
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(this.element);
    this._observer = observer;
  }

  startAutoplay() {
    if (this.autoplayInterval || this.totalSteps <= 1) return;
    var self = this;
    this.autoplayInterval = setInterval(function () {
      if (self.isSnapping) return;
      var next = self.activeIndex + 1;
      if (next >= self.totalSteps) {
        self.wrapForward(0);
      } else {
        self.setActiveStep(next);
      }
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    if (!this.autoplayInterval) return;
    clearInterval(this.autoplayInterval);
    this.autoplayInterval = null;
  }

  pauseAutoplay() {
    this.stopAutoplay();
    if (this.autoplayTimeout) clearTimeout(this.autoplayTimeout);
    var self = this;
    this.autoplayTimeout = setTimeout(function () { self.startAutoplay(); }, 6000);
  }

  /* -------------------------------------------------------
     Activate a step
     ------------------------------------------------------- */
  setActiveStep(index, animate) {
    if (index < 0 || index >= this.totalSteps) return;
    if (animate === undefined) animate = true;
    this.activeIndex = index;

    for (var i = 0; i < this.allTrackItems.length; i++) {
      this.allTrackItems[i].classList.toggle('is-active', i === index);
    }

    this.updateInfo(index);

    for (var j = 0; j < this.labels.length; j++) {
      this.labels[j].classList.toggle('is-active', j === index);
    }

    var self = this;
    requestAnimationFrame(function () { self.updateProgress(); });
  }

  wrapForward(targetIndex) {
    this.setActiveStep(targetIndex, true);
  }

  /* -------------------------------------------------------
     Info text: content + horizontal alignment with active card
     ------------------------------------------------------- */
  updateInfo(index) {
    if (!this.infoEl) return;
    var item = this.allTrackItems[index];
    if (!item) return;

    if (this.infoTitle) this.infoTitle.textContent = item.dataset.title || '';
    if (this.infoDesc) this.infoDesc.textContent = item.dataset.description || '';

    // Compute exact left edge of active card (accounts for gap + flex sizes at any viewport)
    var trackStyle = getComputedStyle(this.track);
    var padLeft = parseFloat(trackStyle.paddingLeft) || 40;
    var gap = parseFloat(trackStyle.gap) || 16;
    var trackWidth = this.track.offsetWidth;
    var contentWidth = trackWidth - padLeft * 2;
    var totalFlex = this.totalSteps - 1 + 1.415;
    var unitWidth = (contentWidth - (this.totalSteps - 1) * gap) / totalFlex;
    var cardLeft = padLeft + index * (unitWidth + gap);

    this.infoEl.style.marginLeft = Math.round(cardLeft) + 'px';
  }

  /* -------------------------------------------------------
     Progress bar
     ------------------------------------------------------- */
  updateProgress() {
    if (!this.labels[this.activeIndex] || !this.progressBar) return;

    // Last step always fills the entire bar
    if (this.activeIndex === this.totalSteps - 1) {
      this.progressActive.style.width = '100%';
      return;
    }

    var label = this.labels[this.activeIndex];
    var barRect = this.progressBar.getBoundingClientRect();
    var labelRect = label.getBoundingClientRect();

    var width = labelRect.right - barRect.left;
    var percentage = Math.max(0, Math.min(100, (width / barRect.width) * 100));
    this.progressActive.style.width = percentage + '%';
  }


  destroy() {
    this.stopAutoplay();
    if (this.autoplayTimeout) clearTimeout(this.autoplayTimeout);
    if (this._observer) this._observer.disconnect();

    this.originalItems.forEach(function (item) { item.classList.remove('is-active'); });
    this.labels.forEach(function (label) { label.classList.remove('is-active'); });
    if (this.progressActive) this.progressActive.style.width = '';
    if (this.infoEl) this.infoEl.style.marginLeft = '';
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTimeline);
} else {
  initTimeline();
}

function initTimeline() {
  var sections = document.querySelectorAll('section.section-timeline');
  sections.forEach(function (section) {
    if (section.hasAttribute('data-timeline-init')) return;
    section.setAttribute('data-timeline-init', 'true');
    setupTimelineSection(section);
  });
}

function setupTimelineSection(section) {
  var mq = window.matchMedia('(max-width: 749px)');

  function handleMode() {
    if (section._timeline) {
      section._timeline.destroy();
      section._timeline = null;
    }
    if (!mq.matches) {
      section._timeline = new Timeline(section);
    }
    // Mobile is fully static — no JS needed
  }

  handleMode();
  mq.addEventListener('change', handleMode);

  section._timelineMQ = mq;
  section._timelineHandleMode = handleMode;
}

// Shopify Theme Editor events
document.addEventListener('shopify:section:load', function (e) {
  var section = e.target.querySelector ? e.target.querySelector('section.section-timeline') || e.target : e.target;
  if (!section.classList || !section.classList.contains('section-timeline')) return;
  section.removeAttribute('data-timeline-init');
  if (section._timeline) {
    section._timeline.destroy();
    section._timeline = null;
  }
  section.setAttribute('data-timeline-init', 'true');
  setupTimelineSection(section);
});

document.addEventListener('shopify:section:unload', function (e) {
  var section = e.target.querySelector ? e.target.querySelector('section.section-timeline') || e.target : e.target;
  if (section._timeline) {
    section._timeline.destroy();
    section._timeline = null;
  }
  if (section._timelineMQ && section._timelineHandleMode) {
    section._timelineMQ.removeEventListener('change', section._timelineHandleMode);
  }
});

document.addEventListener('shopify:block:select', function (e) {
  var section = e.target.closest('section.section-timeline');
  if (!section || !section._timeline) return;
  // Find the block index in the original items
  var block = e.target;
  var items = Array.from(section.querySelectorAll('.timeline__item:not(.is-clone)'));
  var index = items.indexOf(block.closest('.timeline__item'));
  if (index >= 0 && typeof section._timeline.goTo === 'function') {
    section._timeline.goTo(index);
  }
});
